import React, { useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import { AlertBar } from './components/AlertBar';
import { TrustFooter } from './components/TrustFooter';
import { LegalFooter } from './components/LegalFooter';
import { WhatsAppConfirmationModal } from './components/WhatsAppConfirmationModal';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/pAUUzwkew5M2Jdo9P6J2GU/ChatGPT_Image_9_abr_2026,_04_58_39_p.m..png';
const AGE_RANGES = ['25-34', '35-44', '45-54', '55-65', '65+'];
const GOALS = ['Seguro de vida', 'Ahorrar e invertir', 'Planificación de retiro', 'No estoy seguro aún'];
const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
type SectionId = 'age' | 'goal' | 'state' | null;
const validatePhoneNumber = (phone: string): {
  isValid: boolean;
  error?: string;
} => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10) {
    return {
      isValid: false,
      error: 'El número debe tener 10 dígitos'
    };
  }
  const areaCode = digits.substring(0, 3);
  const invalidAreaCodes = ['000', '111', '222', '333', '444', '555', '666', '777', '888', '999'];
  if (invalidAreaCodes.includes(areaCode)) {
    return {
      isValid: false,
      error: 'Código de área inválido'
    };
  }
  if (/^(\d)\1{9}$/.test(digits)) {
    return {
      isValid: false,
      error: 'Número de teléfono inválido'
    };
  }
  const isSequential = (str: string) => {
    for (let i = 0; i < str.length - 1; i++) {
      if (parseInt(str[i + 1]) !== parseInt(str[i]) + 1) {
        return false;
      }
    }
    return true;
  };
  if (isSequential(digits)) {
    return {
      isValid: false,
      error: 'Número de teléfono inválido'
    };
  }
  const fakeNumbers = ['5555555555', '1234567890', '0123456789', '9876543210', '1111111111', '2222222222', '3333333333', '4444444444', '6666666666', '7777777777', '8888888888', '0000000000'];
  if (fakeNumbers.includes(digits)) {
    return {
      isValid: false,
      error: 'Por favor ingresa un número de teléfono válido'
    };
  }
  if (areaCode[0] === '0' || areaCode[0] === '1') {
    return {
      isValid: false,
      error: 'Código de área inválido'
    };
  }
  const exchangeCode = digits.substring(3, 6);
  if (exchangeCode[0] === '0' || exchangeCode[0] === '1') {
    return {
      isValid: false,
      error: 'Número de teléfono inválido'
    };
  }
  return {
    isValid: true
  };
};
export function App() {
  const [formData, setFormData] = useState({
    age: '',
    goal: '',
    state: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [openSection, setOpenSection] = useState<SectionId>('age');
  const [showModal, setShowModal] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneBlurred, setPhoneBlurred] = useState(false);
  const ageRef = useRef<HTMLDivElement>(null);
  const goalRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const handlePhoneChange = (value: string) => {
    let val = value.replace(/\D/g, '');
    if (val.length > 10) val = val.slice(0, 10);
    let formatted = val;
    if (val.length > 6) {
      formatted = `(${val.slice(0, 3)}) ${val.slice(3, 6)}-${val.slice(6)}`;
    } else if (val.length > 3) {
      formatted = `(${val.slice(0, 3)}) ${val.slice(3)}`;
    } else if (val.length > 0) {
      formatted = `(${val}`;
    }
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
    if (phoneError) {
      setPhoneError('');
    }
  };
  const handlePhoneBlur = () => {
    setPhoneBlurred(true);
    const validation = validatePhoneNumber(formData.phone);
    if (!validation.isValid && validation.error) {
      setPhoneError(validation.error);
    }
  };
  const isPhoneValid = () => {
    const validation = validatePhoneNumber(formData.phone);
    return validation.isValid;
  };
  const isSectionComplete = (section: SectionId): boolean => {
    if (!section) return false;
    switch (section) {
      case 'age':
        return !!formData.age;
      case 'goal':
        return !!formData.goal;
      case 'state':
        return !!formData.state;
      default:
        return false;
    }
  };
  const getSectionAnswer = (section: SectionId): string => {
    if (!section) return '';
    switch (section) {
      case 'age':
        return formData.age;
      case 'goal':
        return formData.goal;
      case 'state':
        return formData.state;
      default:
        return '';
    }
  };
  const isFormValid = formData.age && formData.goal && formData.state && formData.firstName.trim().length >= 2 && formData.lastName.trim().length >= 2 && isPhoneValid() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      setPhoneError(phoneValidation.error || 'Número de teléfono inválido');
      setPhoneBlurred(true);
      return;
    }
    if (isFormValid) {
      setShowModal(true);
    }
  };
  const handleWhatsAppConfirm = async () => {
    try {
      const params = new URLSearchParams();
      params.append('age', formData.age);
      params.append('goal', formData.goal);
      params.append('state', formData.state);
      params.append('firstName', formData.firstName);
      params.append('lastName', formData.lastName);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      await fetch('https://services.leadconnectorhq.com/hooks/1GwCc69xxIEbnVuzsYbO/webhook-trigger/0e3ee1df-5045-44c8-8215-63b330329404', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString(),
        mode: 'no-cors'
      });
    } catch (err) {
      console.error('Webhook error:', err);
    }
    const message = `Hola! Acabo de completar el formulario de Life Freedom.

📋 Mis datos:
- Nombre: ${formData.firstName} ${formData.lastName}
- Edad: ${formData.age}
- Objetivo: ${formData.goal}
- Estado: ${formData.state}
- Teléfono: ${formData.phone}
- Email: ${formData.email}

Me gustaría continuar la conversación.`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/13852507792?text=${encodedMessage}`, '_blank');
    setShowModal(false);
  };
  const toggleSection = (section: SectionId) => {
    setOpenSection(openSection === section ? null : section);
  };
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 200);
  };
  const handleSectionComplete = (section: SectionId, nextSection: SectionId, nextRef?: React.RefObject<HTMLDivElement>) => {
    if (section && isSectionComplete(section)) {
      setOpenSection(nextSection);
      if (nextRef) {
        scrollToSection(nextRef);
      }
    }
  };
  const QuestionSection = ({
    id,
    title,
    children,
    sectionRef
  }: {
    id: SectionId;
    title: string;
    children: React.ReactNode;
    sectionRef?: React.RefObject<HTMLDivElement>;
  }) => {
    const isOpen = openSection === id;
    const isComplete = isSectionComplete(id);
    const answer = getSectionAnswer(id);
    return <div ref={sectionRef} className="space-y-1.5" data-id="element-0">
        <div className="flex items-center gap-2" data-id="element-1">
          {isComplete && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-2" />}
          <h3 className="font-semibold text-dark text-sm" data-id="element-3">{title}</h3>
        </div>

        <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden" data-id="element-4">
          <button type="button" onClick={() => toggleSection(id)} className="w-full p-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-5">
            <span className="text-xs text-gray-600" data-id="element-6">
              {answer || 'Selecciona una opción'}
            </span>
            <motion.div animate={{
            rotate: isOpen ? 180 : 0
          }} transition={{
            duration: 0.15
          }} data-id="element-7">
              <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-8" />
            </motion.div>
          </button>
          <AnimatePresence initial={false} data-id="element-9">
            {isOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.15,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-10">
                <div className="p-2.5 pt-0 border-t border-gray-100" data-id="element-11">
                  {children}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>;
  };
  return <div className="min-h-screen flex flex-col bg-light font-sans pt-6" data-id="element-12">
      <AlertBar userState={formData.state || 'tu área'} data-id="element-13" />

      <main className="flex-1 flex flex-col items-center justify-start overflow-auto px-3 py-3" data-id="element-14">
        <div className="flex justify-center mb-3" data-id="element-15">
          <img src={LOGO_URL} alt="Life Freedom" className="h-16 sm:h-20 object-contain" data-id="element-16" />
        </div>

        <div className="w-full max-w-lg" data-id="element-17">
          <div className="text-center mb-3" data-id="element-18">
            <h1 className="text-lg sm:text-2xl font-bold text-dark mb-1" data-id="element-19">
              Obtén Tu Cotización Personalizada
            </h1>
            <p className="text-gray-600 text-xs" data-id="element-20">
              Ahorra hasta un 70% en tu seguro de vida
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-id="element-21">
            {/* Age Section */}
            <QuestionSection id="age" title="¿En qué grupo de edad estás?" sectionRef={ageRef} data-id="element-22">
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-id="element-23">
                {AGE_RANGES.map(range => <button key={range} type="button" onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  age: range
                }));
                handleSectionComplete('age', 'goal', goalRef);
              }} className={`p-2 rounded-lg border-2 font-semibold text-xs transition-all ${formData.age === range ? 'border-primary bg-primary-light text-primary' : 'border-gray-200 bg-white text-dark hover:border-primary'}`} data-id="element-24">
                    {range}
                  </button>)}
              </div>
            </QuestionSection>

            {/* Goal Section */}
            <QuestionSection id="goal" title="¿Qué te gustaría lograr?" sectionRef={goalRef} data-id="element-25">
              <div className="grid grid-cols-1 gap-1.5" data-id="element-26">
                {GOALS.map(goal => <button key={goal} type="button" onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  goal
                }));
                handleSectionComplete('goal', 'state', stateRef);
              }} className={`p-2 rounded-lg border-2 font-medium text-xs transition-all text-left ${formData.goal === goal ? 'border-primary bg-primary-light text-primary' : 'border-gray-200 bg-white text-dark hover:border-primary'}`} data-id="element-27">
                    {goal}
                  </button>)}
              </div>
            </QuestionSection>

            {/* State Section */}
            <QuestionSection id="state" title="¿En qué estado resides?" sectionRef={stateRef} data-id="element-28">
              <div className="relative" data-id="element-29">
                <select value={formData.state} onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  state: e.target.value
                }));
                if (e.target.value) {
                  setOpenSection(null);
                  scrollToSection(nameRef);
                }
              }} className="w-full p-2.5 bg-white rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-0 outline-none text-xs appearance-none cursor-pointer transition-colors" required data-id="element-30">
                  <option value="" data-id="element-31">Selecciona tu estado</option>
                  {US_STATES.map(state => <option key={state} value={state} data-id="element-32">
                      {state}
                    </option>)}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" data-id="element-33">
                  <ChevronDown className="w-4 h-4" data-id="element-34" />
                </div>
              </div>
            </QuestionSection>

            {/* Name Section */}
            <div ref={nameRef} className="space-y-1.5" data-id="element-35">
              <h3 className="font-semibold text-dark text-sm" data-id="element-36">
                ¿Cuál es tu nombre?
              </h3>
              <div className="grid grid-cols-2 gap-2" data-id="element-37">
                <input type="text" value={formData.firstName} onChange={e => setFormData(prev => ({
                ...prev,
                firstName: e.target.value
              }))} placeholder="Nombre" className="w-full p-2.5 bg-white rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-0 outline-none text-xs transition-colors" required data-id="element-38" />
                <input type="text" value={formData.lastName} onChange={e => setFormData(prev => ({
                ...prev,
                lastName: e.target.value
              }))} placeholder="Apellido" className="w-full p-2.5 bg-white rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-0 outline-none text-xs transition-colors" required data-id="element-39" />
              </div>
            </div>

            {/* Phone Section */}
            <div className="space-y-1.5" data-id="element-40">
              <h3 className="font-semibold text-dark text-sm" data-id="element-41">
                Número de teléfono
              </h3>
              <div className="relative" data-id="element-42">
                <input type="tel" value={formData.phone} onChange={e => handlePhoneChange(e.target.value)} onBlur={handlePhoneBlur} placeholder="(555) 123-4567" className={`w-full p-2.5 bg-white rounded-lg border-2 focus:ring-0 outline-none text-xs transition-colors ${phoneError && phoneBlurred ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-primary'}`} required data-id="element-43" />
                {phoneError && phoneBlurred && <div className="absolute right-2 top-1/2 -translate-y-1/2" data-id="element-44">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-45" />
                  </div>}
              </div>
              {phoneError && phoneBlurred && <p className="text-red-500 text-xs flex items-center gap-1" data-id="element-46">
                  <AlertCircle className="w-3 h-3" data-id="element-47" />
                  {phoneError}
                </p>}
            </div>

            {/* Email Section */}
            <div className="space-y-1.5" data-id="element-48">
              <h3 className="font-semibold text-dark text-sm" data-id="element-49">
                Correo electrónico
              </h3>
              <input type="email" value={formData.email} onChange={e => setFormData(prev => ({
              ...prev,
              email: e.target.value
            }))} placeholder="tu@email.com" className="w-full p-2.5 bg-white rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-0 outline-none text-xs transition-colors" required data-id="element-50" />
            </div>

            {/* Submit Button */}
            <motion.button whileHover={{
            scale: isFormValid ? 1.02 : 1
          }} whileTap={{
            scale: isFormValid ? 0.98 : 1
          }} type="submit" disabled={!isFormValid} className="w-full bg-primary hover:bg-primary-hover text-white font-bold text-sm py-3 px-4 rounded-lg shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-3" data-id="element-51">
              Enviar Información
            </motion.button>
          </form>
        </div>
      </main>

      <TrustFooter data-id="element-52" />
      <LegalFooter data-id="element-53" />

      <AnimatePresence data-id="element-54">
        {showModal && <WhatsAppConfirmationModal formData={formData} onConfirm={handleWhatsAppConfirm} onClose={() => setShowModal(false)} data-id="element-55" />}
      </AnimatePresence>
    </div>;
}